<!-- Modal Header -->
<div class="flex items-center justify-between p-6 py-2">
  <div class="flex items-center gap-3">
    <mat-icon class="text-blue-600">search</mat-icon>
    <h2 class="text-lg font-semibold">Localizador de clientes en rutas</h2>
  </div>
  
  <button mat-icon-button (click)="onClose()" class="text-gray-500 hover:text-gray-700">
    <mat-icon>close</mat-icon>
  </button>
</div>

<!-- Search Field (full width below title) -->
<div class="px-6 border-b border-gray-100">
  <mat-form-field appearance="outline" class="w-full">
    <mat-label>Buscar cliente</mat-label>
    <input 
      matInput 
      [formControl]="searchControl"
      placeholder="Nombre, código o nombre comercial..."
      autocomplete="off">
    <mat-icon matPrefix>search</mat-icon>
    <button 
      *ngIf="searchControl.value" 
      matSuffix 
      mat-icon-button 
      (click)="clearSearch()"
      type="button">
      <mat-icon>clear</mat-icon>
    </button>
  </mat-form-field>
</div>

<!-- Modal Content -->
<mat-dialog-content>
  <div class="h-full flex flex-col">
    
    <!-- Loading Indicator -->
    <div *ngIf="isLoading" class="text-center py-8 flex items-center justify-center h-full">
      <mat-spinner diameter="40"></mat-spinner>
      <p class="mt-2 text-gray-500">Cargando clientes...</p>
    </div>

    <!-- Clients Table -->
    <div *ngIf="!isLoading" class="overflow-hidden bg-white rounded-lg border border-gray-200">
      <div class="max-h-[600px] overflow-y-auto">
        <table mat-table [dataSource]="dataSource" multiTemplateDataRows class="w-full">
        
          <!-- Expand Column -->
          <ng-container matColumnDef="expand">
            <th mat-header-cell *matHeaderCellDef class="w-16"></th>
            <td mat-cell *matCellDef="let client" class="py-2">
              <button 
                mat-icon-button 
                (click)="toggleExpandRow(client); $event.stopPropagation()"
                [disabled]="!hasRoutes(client)"
                matTooltip="Ver rutas"
                class="transition-transform"
                [class.rotate-90]="expandedElement === client">
                <mat-icon *ngIf="hasRoutes(client)">chevron_right</mat-icon>
                <mat-icon *ngIf="!hasRoutes(client)" class="text-gray-300">block</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- Client Info Column -->
          <ng-container matColumnDef="info">
            <th mat-header-cell *matHeaderCellDef class="font-semibold">Cliente</th>
            <td mat-cell *matCellDef="let client" class="py-4">
              <div>
                <div class="flex items-center gap-2">
                  <p class="font-medium text-gray-900">
                    <span *ngIf="client.externalKeys?.code">{{ client.externalKeys.code }} - </span>{{ client.name }}
                  </p>
                  <mat-icon
                    svgIcon="heroicons_outline:rocket-launch"
                    class="icon-size-4 cursor-pointer hover:text-blue-600 hover:animate-bounce"
                    matTooltip="Ver en Socios de Negocio"
                    (click)="navigateToClient(client); $event.stopPropagation()">
                  </mat-icon>
                </div>
                <p *ngIf="client.commercialName" class="text-sm text-gray-500">{{ client.commercialName }}</p>
                <div *ngIf="hasRoutes(client)" class="mt-1">
                  <mat-chip-set class="routes-chip-set">
                    <mat-chip class="!min-h-[24px] !py-0 !text-xs route-total-chip">
                      <mat-icon class="!w-4 !h-4 !text-[14px] mr-1">route</mat-icon>
                      {{ getRoutesCount(client) }} {{ getRoutesCount(client) === 1 ? 'ruta' : 'rutas' }}
                    </mat-chip>
                    <mat-chip *ngIf="getRoutesWithFrequency(client) > 0" 
                              class="!min-h-[24px] !py-0 !text-xs route-with-frequency-chip"
                              matTooltip="Rutas con frecuencia configurada">
                      <mat-icon class="!w-4 !h-4 !text-[14px] mr-1">check_circle</mat-icon>
                      {{ getRoutesWithFrequency(client) }}
                    </mat-chip>
                    <mat-chip *ngIf="getRoutesWithoutFrequency(client) > 0" 
                              class="!min-h-[24px] !py-0 !text-xs route-without-frequency-chip"
                              matTooltip="Rutas sin frecuencia configurada">
                      <mat-icon class="!w-4 !h-4 !text-[14px] mr-1">warning</mat-icon>
                      {{ getRoutesWithoutFrequency(client) }}
                    </mat-chip>
                  </mat-chip-set>
                </div>
              </div>
            </td>
          </ng-container>
  
          <!-- Location Column -->
          <ng-container matColumnDef="location">
            <th mat-header-cell *matHeaderCellDef class="font-semibold">Ubicación</th>
            <td mat-cell *matCellDef="let client">
              <div class="text-sm">
                <p class="text-gray-900">{{ getClientAddress(client) }}</p>
                <p *ngIf="client.principalAddress?.locationOne?.name" class="text-gray-500">
                  {{ client.principalAddress.locationOne.name }}
                </p>
              </div>
            </td>
          </ng-container>
  
          <!-- Territorios Column -->
          <ng-container matColumnDef="territorios">
            <th mat-header-cell *matHeaderCellDef class="font-semibold">Territorios</th>
            <td mat-cell *matCellDef="let client">
              <div class="text-sm">
                @if (getTerritories(client).length > 0) {
                  <div class="flex flex-wrap gap-1 items-center">
                    <!-- Territorios visibles (máximo 4) -->
                    @for (territory of getTerritoriesToDisplay(client).visible; track territory._id) {
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                        <mat-icon class="!w-3 !h-3 !text-[12px] mr-1">location_on</mat-icon>
                        {{ territory.externalKeys?.code ? (territory.externalKeys.code + ' - ' + territory.name) : territory.name }}
                      </span>
                    }

                    <!-- Badge "+N más" con popover -->
                    @if (getTerritoriesToDisplay(client).hasMore) {
                      <span
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-200 text-gray-700 cursor-pointer hover:bg-gray-300"
                        [matMenuTriggerFor]="territoriesMenu"
                        #menuTrigger="matMenuTrigger"
                        (mouseenter)="menuTrigger.openMenu()"
                        (mouseleave)="closeMenuWithDelay(menuTrigger)">
                        +{{ getTerritoriesToDisplay(client).moreCount }} más
                      </span>

                      <mat-menu #territoriesMenu="matMenu" [hasBackdrop]="false" class="territories-popover">
                        <div
                          class="p-3 max-w-xs max-h-48 overflow-y-auto"
                          (mouseenter)="cancelMenuClose()"
                          (mouseleave)="menuTrigger.closeMenu()">
                          <div class="text-xs font-medium text-gray-500 mb-2">
                            Todos los territorios ({{ getTerritoriesToDisplay(client).total }})
                          </div>
                          <div class="flex flex-wrap gap-1">
                            @for (territory of getTerritories(client); track territory._id) {
                              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                                <mat-icon class="!w-3 !h-3 !text-[12px] mr-1">location_on</mat-icon>
                                {{ territory.externalKeys?.code ? (territory.externalKeys.code + ' - ' + territory.name) : territory.name }}
                              </span>
                            }
                          </div>
                        </div>
                      </mat-menu>
                    }
                  </div>
                } @else {
                  <span class="text-gray-400">Sin territorios</span>
                }
              </div>
            </td>
          </ng-container>
  
          <!-- Actions Column -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="font-semibold">Asociar a territorio</th>
            <td mat-cell *matCellDef="let client">
              <button 
                mat-icon-button 
                color="accent"
                matTooltip="Asignar Territorio"
                (click)="openAssignTerritoryDialog(client); $event.stopPropagation()">
                <mat-icon>add_location</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- Expanded Detail Row -->
          <ng-container matColumnDef="expandedDetail">
            <td mat-cell *matCellDef="let client" [attr.colspan]="displayedColumns.length">
              <div class="overflow-hidden" 
                   [@detailExpand]="client == expandedElement ? 'expanded' : 'collapsed'">
                <div class="p-4">
                  <h3 class="text-md font-semibold mb-3 text-gray-700">
                    <mat-icon class="inline-block align-middle mr-2">route</mat-icon>
                    Rutas Asignadas
                  </h3>
                  
                  <!-- Routes Table -->
                  <div class="bg-white rounded border border-gray-200 overflow-hidden">
                    <table class="w-full">
                      <thead class="bg-gray-100">
                        <tr>
                          <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Nombre de Ruta</th>
                          <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Función</th>
                          <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Frecuencias</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let subRoute of client.subRoutes" 
                            [class.route-with-frequency]="hasFrequencies(subRoute)"
                            [class.route-without-frequency]="!hasFrequencies(subRoute)">
                          <td class="px-4 py-3 text-sm font-medium">
                            <div class="flex items-center gap-2">
                              <mat-icon *ngIf="hasFrequencies(subRoute)" 
                                        class="frequency-status-icon has-frequency"
                                        matTooltip="Esta ruta tiene frecuencias configuradas">
                                check_circle
                              </mat-icon>
                              <mat-icon *ngIf="!hasFrequencies(subRoute)" 
                                        class="frequency-status-icon no-frequency"
                                        matTooltip="Esta ruta NO tiene frecuencias configuradas">
                                warning
                              </mat-icon>
                              <span>{{ subRoute.name }}</span>
                            </div>
                          </td>
                          <td class="px-4 py-3 text-sm">
                            <span *ngIf="subRoute.function" 
                                  class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                              {{ subRoute.function.name }}
                            </span>
                            <span *ngIf="!subRoute.function" class="text-gray-400">Sin función</span>
                          </td>
                          <td class="px-4 py-3 text-sm">
                            <div class="flex items-center gap-2">
                              <app-client-frequency-display
                                [subRouteId]="subRoute._id"
                                [subRouteNormalId]="subRoute.id"
                                [businessPartnerId]="client._id"
                                [businessPartnerNormalId]="client.id"
                                [frequenciesFromThisSubRoute]="subRoute.frequencies || []"
                                (frequencyUpdated)="onFrequencyUpdated($event)"
                                (refreshRequested)="onRefreshRequested()">
                              </app-client-frequency-display>
                              <span *ngIf="!hasFrequencies(subRoute)" 
                                    class="no-frequency-badge">
                                Sin frecuencia
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- No Routes Message -->
                  <div *ngIf="!client.subRoutes || client.subRoutes.length === 0" 
                       class="text-center py-8 bg-white rounded border border-gray-200">
                    <mat-icon class="text-gray-300 !w-12 !h-12 !text-[48px]">route</mat-icon>
                    <p class="mt-2 text-gray-500">Este cliente no tiene rutas asignadas</p>
                  </div>
                </div>
              </div>
            </td>
          </ng-container>
  
          <!-- Header and Row Definitions -->
          <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;" 
              [class.expanded-row]="expandedElement === row"></tr>
          <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" 
              class="detail-row"></tr>
        </table>
  
        </div>
      <!-- Empty State -->
      <div *ngIf="dataSource.data.length === 0 && !isLoading" 
           class="text-center py-12 text-gray-500">
        <mat-icon class="w-16 h-16 mx-auto mb-4 text-gray-300">search</mat-icon>
        <h3 class="text-lg font-medium mb-2">
          <span *ngIf="searchControl.value; else noSearchTitle">
            No se encontraron clientes
          </span>
          <ng-template #noSearchTitle>
            Buscar clientes
          </ng-template>
        </h3>
        <p class="text-sm">
          <span *ngIf="searchControl.value; else noSearchTerm">
            No hay resultados para "{{ searchControl.value }}"
          </span>
          <ng-template #noSearchTerm>
            Utiliza el campo de búsqueda para encontrar clientes
          </ng-template>
        </p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-auto">
      <mat-paginator
        [length]="totalItems"
        [pageSize]="pageSize"
        [pageIndex]="currentPage"
        [pageSizeOptions]="pageSizeOptions"
        (page)="onPageChange($event)"
        showFirstLastButtons
        class="border-t border-gray-200">
      </mat-paginator>
    </div>
  </div>
</mat-dialog-content>
